import { useId } from 'react';
import { cx } from '@/lib/utils';

// Intrinsic SVG viewBox; height/width derive from this ratio.
const VIEWBOX_WIDTH = 202;
const VIEWBOX_HEIGHT = 380;

interface BadgeProps {
  /** Rendered width in pixels; height scales to keep the SVG aspect ratio */
  size?: number;
  /** Additional CSS classes */
  className?: string;
  /** Accessible label; when omitted the illustration is decorative (aria-hidden) */
  title?: string;
  /** Click handler */
  onClick?: () => void;
}

/**
 * Line-art illustration of a hand holding a lanyard NFC badge, exported
 * verbatim from Figma (`Group 6`, node 578:1429). The drop-shadow filter id is
 * made instance-unique via useId so multiple Badges on a page don't collide,
 * matching the pattern in GlowingCircle/Hexagon.
 */
export default function Badge({
  size = VIEWBOX_WIDTH,
  className = '',
  title,
  onClick,
}: BadgeProps) {
  const id = useId();
  const filterId = `badge-shadow-${id}`.replace(/[^a-zA-Z0-9_-]/g, '-');
  const height = (size * VIEWBOX_HEIGHT) / VIEWBOX_WIDTH;

  return (
    <svg
      className={cx(className, onClick && 'cursor-pointer')}
      width={size}
      height={height}
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      onClick={onClick}
    >
      {title ? <title>{title}</title> : null}
      <g filter={`url(#${filterId})`}>
        <path
          d="M73.9563 278.188C68.318 283.98 59.6092 285.434 62.2607 290.519C62.8832 291.716 65.2894 292.745 66.8994 291.859C74.8421 287.486 81.1089 281.67 86.7771 274.635L112.856 242.267C115.405 239.102 118.314 236.404 122.361 235.662C126.538 234.896 130.824 236.362 133.368 239.264C138.808 245.474 135.624 253.078 130.926 260.293L119.146 278.404C109.869 292.661 107.14 296.101 102.644 313.811C98.9993 328.158 92.99 341.206 83.2576 352.401L70.1795 365.641C68.9106 366.921 68.5155 369.153 69.3056 370.295C70.5206 372.048 72.4479 372.264 74.5009 371.773L85.8912 360.4C91.7211 354.578 96.0545 347.776 100.131 340.453C106.786 345.945 115.806 346.537 122.558 341.542C128.226 337.347 130.716 330.467 129.818 323.21C137.773 319.68 142.549 311.848 141.538 303.065L139.413 293.499L172.471 296.221C180.084 296.849 187.578 292.332 188.26 284.052L197.992 165.721C197.729 157.98 191.875 153.098 184.447 152.577L157.842 150.722L165.36 123.213L185.094 50.4905C187.195 42.7426 187.123 35.0725 185.226 27.4502C181.084 10.8177 166.228 0.0963225 149.247 0.000595814C129.429 -0.107097 113.532 14.3955 112.215 34.2887C111.706 41.9409 112.939 49.0725 114.891 56.6768L134.577 133.593L138.467 149.227L114.627 147.689C106.505 147.162 101.1 153.48 100.4 161.055L96.5094 202.875L76.542 209.439C60.3873 214.746 47.1894 226.741 36.0146 239.431C28.5867 247.867 23.4571 257.817 22.3438 269.25L16.4362 329.713L4.33965 352.293C3.42387 354.004 4.49527 356.397 5.94376 357.097C7.75734 357.983 9.97194 357.271 11.0014 355.362L21.44 335.941C22.9424 333.153 23.912 330.066 24.2173 326.841L29.3408 272.14C31.2801 251.409 43.9034 240.514 58.6994 227.453C63.6912 223.666 68.713 220.663 74.3573 217.946L95.7373 210.725L93.3431 238.767C84.1914 242.076 76.4642 247.562 71.8255 256.111C67.9349 263.273 69.1021 271.158 73.9742 278.2L73.9563 278.188ZM146.195 149.639L134.619 104.367L120.583 49.1742C119.32 44.2024 119.027 39.2605 119.422 34.2887C120.691 18.1887 133.817 6.76728 149.822 7.15617C164.851 7.52113 177.085 18.081 178.983 33.122C179.707 38.8477 179.132 44.5255 177.672 50.1315L170.992 75.7623L150.45 150.034C148.87 150.04 147.338 149.915 146.201 149.633L146.195 149.639ZM135.086 267.126C137.797 262.956 140.077 258.965 142.029 254.711C145.375 245.881 142.633 236.571 135.074 231.474C127.514 226.376 118.165 227.507 111.168 233.436L100.011 246.658L108.067 159.26C108.295 156.771 111.724 154.803 114.095 154.97L183.244 159.834C186.644 160.074 191.043 161.33 190.69 165.62L181.149 281.754C180.82 285.799 178.827 289.323 174.236 288.952L123.564 284.841L135.08 267.12L135.086 267.126ZM78.9242 273.085C75.704 267.784 75.8118 262.172 79.1636 257.482C82.689 252.552 87.1063 248.705 92.7266 246.443L91.9006 257.069L78.9242 273.079V273.085ZM111.521 309.623C112.532 303.047 115.447 297.406 118.889 291.865L131.44 292.799C134.756 300.852 136.474 310.107 129.782 315.091C123.48 319.788 115.657 316.91 111.521 309.617V309.623ZM118.314 335.84C113.442 339.1 107.313 337.844 103.47 334.039L109.276 317.915C112.425 322.211 117.099 324.424 122.361 324.568C123.097 328.858 121.888 333.076 118.32 335.84H118.314Z"
          fill="black"
        />
      </g>
      <path
        d="M136.953 174.786L158.758 176.329C161.595 176.533 163.899 174.929 164.48 172.237C164.887 170.34 163.492 166.392 160.583 166.17L136.69 164.381C133.595 164.148 131.381 167.145 131.381 169.503C131.381 172.692 133.529 174.54 136.953 174.786Z"
        fill="black"
      />
      <defs>
        <filter
          id={filterId}
          x="0"
          y="0"
          width="201.992"
          height="380.002"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="4" />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_578_1429" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_578_1429" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}
